<template>
  <div id="consult-form" class="py-12 text-slate md:py-[5rem] lg:py-[6rem]">
    <div class="container">
      <div class="mb-12 flex flex-col md:items-center md:text-center">
        <div
          v-if="data.title"
          class="title mb-6 text-3xl uppercase leading-none text-slate md:text-6xl"
          v-html="data.title"
        />

        <div
          v-if="data.text"
          class="mb-6 w-[665px] max-w-full text-md leading-[1.8rem] text-slate"
          v-html="data.text"
        />

        <div
          v-if="data.bottomText"
          class="text-sm leading-[1.25rem] text-slate-lighten4"
          v-html="data.bottomText"
        />
      </div>

      <client-only>
        <form
          class="mx-auto max-w-[664px]"
          autocomplete="off"
          @submit.prevent="submitForm"
        >
          <div class="mb-12 flex flex-col gap-y-4">
            <div class="flex flex-col gap-4 md:grid md:grid-cols-2">
              <UiInput
                v-model.trim="v$.name.$model"
                :placeholder="$t('name') + '*'"
                :error="errorMessage(v$.name)"
                required
              ></UiInput>

              <UiInput
                v-model.trim="v$.phone.$model"
                :placeholder="$t('phone_number') + '*'"
                :error="errorMessage(v$.phone) && $t('validation_phone')"
                required
                type="tel"
              ></UiInput>

              <UiInput
                v-model.trim="v$.email.$model"
                :placeholder="$t('email') + '*'"
                :error="errorMessage(v$.email)"
                required
                type="email"
              ></UiInput>

              <UiSelect
                v-model.trim="v$.country.$model"
                :placeholder="$t('choose_your_country') + '*'"
                :options="countries"
                :error="errorMessage(v$.country)"
                item-id="code"
                item-text="name"
              ></UiSelect>
            </div>

            <span class="block h-full">
              <UiInput
                v-model.trim="v$.company.$model"
                :placeholder="$t('your_company') + '*'"
                :error="errorMessage(v$.company)"
                required
                type="text"
              ></UiInput>
            </span>
          </div>

          <UiCheckbox v-model="confirm" class="mb-6">
            {{ $t('i_consent_to_the_processing') }}
          </UiCheckbox>
          <span class="mb-12">
            <UiCheckbox v-model="formData.personalData">
              {{ $t('personal_data') }}
            </UiCheckbox>
          </span>

          <UiRecaptcha
            class="my-6 flex justify-center md:my-12"
            @error="recaptcha.setToken('')"
            @success="recaptcha.setToken($event)"
            @expired="recaptcha.setToken('')"
          />

          <div class="mx-auto w-full max-w-[438px] justify-center">
            <UiButton
              type="submit"
              size="lg"
              block
              :loading="formPending || recaptcha.isBusy.value"
              :disabled="isSubmitDisabled(v$.$invalid)"
            >
              {{ $t('submit') }}
            </UiButton>
          </div>
        </form>
      </client-only>
    </div>
  </div>
</template>

<script setup lang="ts">
import { nextTick, reactive, ref, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';

import {
  useForm,
  useCountries,
  useDomain,
  useGtm,
  useModalDialog,
  useSiteConfigState,
  useValidationRules,
} from '~/composables';
import UiRecaptcha from '~/components/ui/UiRecaptcha.vue';

interface FormData {
  name: string;
  phone: string;
  email: string;
  country: string;
  company: string;
  personalData: boolean;
}

const FORM_DEFAULTS = {
  name: '',
  phone: '',
  email: '',
  country: '',
  company: '',
  personalData: false,
};

interface Props {
  data?: Record<string, any>;
}

const props = withDefaults(defineProps<Props>(), {
  data: () => ({}),
});

const { domain } = useDomain();
const { activeLanguage } = useSiteConfigState();

const { formPending, confirm, submit, recaptcha, isSubmitDisabled } = useForm();
const tryDemoModal = useModalDialog('try-demo-form');
const infoModal = useModalDialog('try-demo-form-info');
const { softwareTrialFormUuid } = useRuntimeConfig().public.frontkit;
const { push: pushGtmEvent } = useGtm();

const formData = reactive<FormData>({
  ...FORM_DEFAULTS,
});

const {
  required,
  checked,
  email,
  publicEmail,
  phone,
  alphaSpaces,
  errorMessage,
} = useValidationRules();

const v$ = useVuelidate(
  {
    name: { required, alphaSpaces },
    phone: { required, phone },
    email: { required, email, publicEmail },
    country: { required },
    company: { required },
    personalData: { checked },
  },
  formData,
);
const isFormStartEventSended = ref(false);

const { getCountriesList } = useCountries();
const countries = getCountriesList();
const openFile = () => {
  if (!props.data.file?.link) return;
  window.open(props.data.file.link, '_self');
};

const clearForm = () => {
  confirm.value = false;

  Object.assign(formData, {
    ...FORM_DEFAULTS,
  });

  nextTick(() => v$.value.$reset());
};

const submitForm = async () => {
  try {
    const formId = softwareTrialFormUuid;
    await submit({ ...createFormPayload(formData) }, formId);
    openFile();
    clearForm();
    tryDemoModal.close();
    infoModal.open();
  } catch (error) {
    console.error('Error submitting form', error);
  }
};

const pushFormStartEvent = () => {
  if (!isFormStartEventSended.value) {
    pushGtmEvent({
      event: 'form_start',
    });
  }
  isFormStartEventSended.value = true;
};

const createFormPayload = (formModel: FormData) => {
  return {
    lang: activeLanguage.value,
    website_url: domain.value,
    lead_source: window.location.href,

    first_name: formModel.name,
    mobile_phone: formModel.phone,
    email: formModel.email,
    country: formModel.country,
    company_name: formModel.company,
  };
};

watch(formData, () => {
  pushFormStartEvent();
});
watch(confirm, () => {
  pushFormStartEvent();
});
</script>
